import { GlassCard } from '@/components/design-system';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Address, Time } from '@/lib/db-client';
import { updateListings, type ListingsResponse } from '@/lib/collections/listings';
import { getPayments, setPayments } from '@/lib/collections/payments';
import { cn } from '@/lib/utils';
import { PLASTIC_TYPE_LABELS, formatCentsAsUsd, isPlasticType } from '@/utils/plastic-rates';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarClock, Check, Copy, ExternalLink, Loader2, MapPin, Package, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type PayPhase = 'idle' | 'paying' | 'confirming' | 'paid';

interface PickupCardProps {
  listing: ListingsResponse;
  userAddress: string;
}

const STATUS_STYLES: Record<string, string> = {
  accepted: 'border-accent/40 bg-accent/10 text-accent',
  scheduled: 'border-[hsl(var(--amber))]/40 bg-[hsl(var(--amber))]/10 text-[hsl(var(--amber))]',
  completed: 'border-border/60 bg-secondary/40 text-muted-foreground',
};

function shortSig(sig: string): string {
  return sig.length > 10 ? `${sig.slice(0, 5)}…${sig.slice(-5)}` : sig;
}

async function waitForTxSignature(listingId: string, attempts = 8, delayMs = 450): Promise<string | undefined> {
  for (let i = 0; i < attempts; i++) {
    const payment = await getPayments(listingId);
    if (payment?.tarobase_transaction_hash) return payment.tarobase_transaction_hash;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return undefined;
}

/** One accepted/scheduled/completed listing this company is handling — surfaces the schedule step and the onchain Complete & Pay settlement flow. */
export function PickupCard({ listing, userAddress }: PickupCardProps) {
  const [scheduleValue, setScheduleValue] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [payPhase, setPayPhase] = useState<PayPhase>('idle');
  const [txSignature, setTxSignature] = useState(listing.txSignature ?? '');

  const minLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  const handleSchedule = async () => {
    if (!scheduleValue) {
      toast.error('Pick a date and time first.');
      return;
    }
    const ms = new Date(scheduleValue).getTime();
    if (Number.isNaN(ms) || ms <= Date.now()) {
      toast.error('Pick a time in the future.');
      return;
    }
    setScheduling(true);
    try {
      const success = await updateListings(listing.id, {
        scheduledFor: Math.floor(ms / 1000),
        status: 'scheduled',
      });
      if (success) {
        toast.success('Pickup scheduled.');
      } else {
        toast.error('Could not schedule this pickup.');
      }
    } catch (err) {
      console.error('Schedule pickup failed', err);
      toast.error('Something went wrong scheduling this pickup.');
    } finally {
      setScheduling(false);
    }
  };

  const handleCompleteAndPay = async () => {
    setPayPhase('paying');
    try {
      const amt = listing.estimatedValue * 10000; // cents -> USDC base units (6 decimals)
      const paid = await setPayments(listing.id, {
        payer: Address.publicKey(userAddress),
        recipient: Address.publicKey(listing.createdBy),
        amt,
      });

      if (!paid) {
        toast.error('Payment failed or was rejected.');
        setPayPhase('idle');
        return;
      }

      setPayPhase('confirming');
      const sig = await waitForTxSignature(listing.id);
      if (!sig) {
        toast.error('Payment sent but the transaction signature has not confirmed yet. Try again shortly.');
        setPayPhase('idle');
        return;
      }

      const completed = await updateListings(listing.id, {
        status: 'completed',
        txSignature: sig,
        completedAt: Time.Now,
      });

      if (!completed) {
        toast.error('Payment settled onchain, but marking the listing complete failed.');
        setPayPhase('idle');
        return;
      }

      setTxSignature(sig);
      setPayPhase('paid');
      toast.success('Paid and completed — settlement confirmed onchain.');
    } catch (err) {
      console.error('Complete & pay failed', err);
      toast.error('Something went wrong settling this payment.');
      setPayPhase('idle');
    }
  };

  const copySig = async () => {
    if (!txSignature) return;
    try {
      await navigator.clipboard.writeText(txSignature);
      toast.success('Signature copied.');
    } catch {
      toast.error('Could not copy.');
    }
  };

  const isCompletedFromData = listing.status === 'completed';

  return (
    <GlassCard padding="sm" className="flex h-full flex-col">
      <div className="flex gap-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary/50">
          {listing.photoUrl ? (
            <img src={listing.photoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">
              {isPlasticType(listing.plasticType) ? PLASTIC_TYPE_LABELS[listing.plasticType] : listing.plasticType}
            </span>
            <Badge variant="outline" className={STATUS_STYLES[listing.status] ?? STATUS_STYLES.accepted}>
              {listing.status}
            </Badge>
          </div>
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {listing.locationName}, {listing.city}
          </p>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-sm font-medium text-foreground">{listing.estimatedKg} kg</span>
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-sm font-bold text-transparent">
              {formatCentsAsUsd(listing.estimatedValue)}
            </span>
          </div>
        </div>
      </div>

      {/* Accepted — schedule the pickup */}
      {listing.status === 'accepted' && (
        <div className="mt-4 space-y-2 border-t border-border/60 pt-4">
          <label className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" /> Schedule pickup
          </label>
          <Input
            type="datetime-local"
            min={minLocal}
            value={scheduleValue}
            onChange={(e) => setScheduleValue(e.target.value)}
          />
          <Button
            onClick={handleSchedule}
            disabled={scheduling}
            className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
          >
            {scheduling ? 'Scheduling...' : 'Confirm schedule'}
          </Button>
        </div>
      )}

      {/* Scheduled — settle payment */}
      {listing.status === 'scheduled' && !isCompletedFromData && (
        <div className="mt-4 border-t border-border/60 pt-4">
          <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 text-[hsl(var(--amber))]" />
            {listing.scheduledFor
              ? new Date(listing.scheduledFor * 1000).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })
              : 'Pickup scheduled'}
          </p>

          <AnimatePresence mode="wait">
            {payPhase === 'idle' && (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Button
                  onClick={handleCompleteAndPay}
                  className="w-full bg-[hsl(var(--amber))] text-background hover:bg-[hsl(var(--amber))]/90"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Complete &amp; Pay
                </Button>
              </motion.div>
            )}

            {(payPhase === 'paying' || payPhase === 'confirming') && (
              <motion.div
                key="processing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2 rounded-xl border border-[hsl(var(--amber))]/30 bg-[hsl(var(--amber))]/[0.06] py-3"
              >
                <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--amber))]" />
                <span className="text-sm font-medium text-[hsl(var(--amber))]">
                  {payPhase === 'paying' ? 'Paying onchain…' : 'Confirming settlement…'}
                </span>
              </motion.div>
            )}

            {payPhase === 'paid' && (
              <motion.div
                key="paid"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-[hsl(var(--amber))]/30 bg-[hsl(var(--amber))]/[0.08] p-3 text-center"
              >
                <div className="mx-auto mb-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--amber))]/20">
                  <Check className="h-4 w-4 text-[hsl(var(--amber))]" strokeWidth={2.5} />
                </div>
                <p className="text-sm font-semibold text-[hsl(var(--amber))]">Paid</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Completed */}
      {isCompletedFromData && (
        <div className="mt-4 border-t border-border/60 pt-4">
          <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/30 px-3 py-2.5">
            <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-[hsl(var(--amber))]" /> Paid &amp; completed
            </span>
            {txSignature && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={copySig}
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                  title="Copy signature"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a
                  href={`https://solscan.io/tx/${txSignature}`}
                  target="_blank"
                  rel="noreferrer"
                  className={cn('flex items-center gap-1 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground')}
                  title="View on Solscan"
                >
                  <span className="font-mono text-[11px]">{shortSig(txSignature)}</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </GlassCard>
  );
}

export default PickupCard;
